/**
 * Interface representing the input required to compute elapsed time for a timer block, including
 * start time, pause time, and accumulated pause duration.
 *
 * @author sadatmalik
 */
export interface TimerElapsedInput {
  startedAt: string;
  pausedAt: string | null;
  accumulatedPauseMs: number | null | undefined;
}

/**
 * Utility functions for computing elapsed time and plan durations for timer blocks.
 *
 * @author sadatmalik
 */
export function computePlanMs(durationMinutes: number | null | undefined): number {
  if (durationMinutes == null || durationMinutes <= 0) {
    return 0;
  }
  return durationMinutes * 60_000;
}

/**
 * Formats elapsed milliseconds as `MM:SS` or `H:MM:SS`.
 *
 * @param value - Elapsed time in milliseconds.
 * @returns A user-friendly elapsed time string.
 */
export function formatElapsedMs(value: number): string {
  const totalSeconds = Math.max(0, Math.floor(value / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/**
 * Formats overrun milliseconds as spoken words next to the timer readout.
 * Minute granularity: "5 min over", "1h 5m over".
 */
export function formatOverrunWords(value: number): string {
  if (value <= 0) {
    return 'over';
  }

  const totalMinutes = Math.max(1, Math.floor(value / 60_000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}m over` : `${hours}h over`;
  }

  return `${totalMinutes} min over`;
}

/**
 * Computes the active elapsed milliseconds for a timer block, accounting for pauses.
 *
 * @param input - The timer block's timing information, including start time, pause time, and accumulated pause duration.
 * @param nowMs - The current time in milliseconds since the epoch.
 * @returns The total active elapsed milliseconds for the timer block.
 *
 * @author sadatmalik
 */
export function computeActiveElapsedMs(input: TimerElapsedInput, nowMs: number): number {
  const startedAtMs = Date.parse(input.startedAt);
  if (Number.isNaN(startedAtMs)) {
    return 0;
  }

  const wallMs = Math.max(0, nowMs - startedAtMs);
  const accumulatedPauseMs = Math.max(0, input.accumulatedPauseMs ?? 0);

  let openPauseMs = 0;
  if (input.pausedAt) {
    const pausedAtMs = Date.parse(input.pausedAt);
    if (!Number.isNaN(pausedAtMs) && nowMs >= pausedAtMs) {
      openPauseMs = nowMs - pausedAtMs;
    }
  }

  return Math.max(0, wallMs - accumulatedPauseMs - openPauseMs);
}
